import {
    Alert,
    ColorIndicator,
    ConfirmModal,
    DataTable,
    Icon,
    IconButton,
    Loading,
    PageTitle,
    StyleFunction,
    Surface,
    useAuth,
    useMerchantConfig,
    useModal,
    usePagination,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQCmpProductsQuery,
    useCmpProductDeleteMutation,
    useCmpProductsQuery,
} from "graphql-sdk";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback } from "react";
import { RootStackScreenProps } from "../../navigation";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";
import { formatAmount, formatDateTime } from "lib";
import { useHandleMutationError } from "../../hooks";

type ScreenProps = RootStackScreenProps<"CMP_PRODUCTS">;

export function CmpProductListScreen(_: ScreenProps) {
    const auth = useAuth();
    auth.enforce(["merchant.cmp"]);

    const styles = useThemedStyle(styleFunc);
    const [{ locale }] = useAdminSession(["locale"]);
    const { currency } = useMerchantConfig();
    const theme = useTheme();
    const [t] = useTranslation();

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "name",
        initialSortDirection: "ASC",
    });

    const { render } = useModal();
    const [cmpProductDelete] = useCmpProductDeleteMutation();
    const { handleMutationError } = useHandleMutationError();

    const { data, loading, error, refetch } = useCmpProductsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
        },
        fetchPolicy: "no-cache",
    });

    const handleDelete = useCallback(
        async (id: string) => {
            if (
                !(await render(onClose => (
                    <ConfirmModal
                        headerText={t(
                            "backoffice.cmp_product.delete",
                            "Delete CMP product?"
                        )}
                        bodyText={t(
                            "backoffice.cmp_product.delete_explain",
                            "If you choose to delete this CMP product, the product will be deleted and removed from any layouts using the product.\n\nAre you sure, you want to delete this?"
                        )}
                        onClose={onClose}
                    />
                )))
            ) {
                return;
            }

            await handleMutationError(
                async () =>
                    await cmpProductDelete({
                        variables: {
                            id: id,
                        },
                    }),
                t("backoffice.cmp_product.deleted", "CMP product deleted"),
                async () => {
                    await refetch();
                }
            );
        },
        [handleMutationError, cmpProductDelete, refetch, render, t]
    );

    const renderCmpProductRow = useCallback(
        (item: GQCmpProductsQuery["cmpProducts"]["data"][0], rowIndex) => (
            <DataTable.Row
                style={rowIndex % 2 ? theme.styles.oddRow : undefined}
                key={`cmpProductId_${item.id}`}
            >
                <DataTable.Cell style={styles.iconColumn}>
                    <ColorIndicator
                        status={item.active ? "success" : "warning"}
                    />
                </DataTable.Cell>
                <DataTable.Cell>{item.name}</DataTable.Cell>
                <DataTable.Cell>{item.externalId}</DataTable.Cell>
                <DataTable.Cell>{item.type}</DataTable.Cell>
                <DataTable.Cell style={styles.iconColumn}>
                    {item.sms ? t("common.yes", "Yes") : t("common.no", "No")}
                </DataTable.Cell>
                <DataTable.Cell numeric>
                    {formatAmount(item.amount, currency, {
                        locale: locale as AvailableLocale,
                    })}
                </DataTable.Cell>
                <DataTable.Cell style={styles.sequenceColumn} numeric>
                    {item.sequence}
                </DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.createdAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.updatedAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.iconColumn}>
                    <IconButton
                        color={theme.colors.secondary}
                        name="delete"
                        onPress={() => handleDelete(item.id)}
                        testID={"delete:" + item.name}
                    />
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [
            currency,
            handleDelete,
            locale,
            styles.iconColumn,
            styles.sequenceColumn,
            t,
            theme.colors.secondary,
            theme.styles.oddRow,
        ]
    );

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error?.message,
                    }
                )}
            </Alert>
        );
    }

    return (
        <AdminContainer>
            <PageTitle>
                {t("backoffice.cmp_product.cmp_products", "CMP products")}
            </PageTitle>
            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "active" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("active")}
                            style={styles.iconColumn}
                        >
                            {t("backoffice.common.active", "Active")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "name" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("name")}
                        >
                            {t("common.name", "Name")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "externalId"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("externalId")}
                        >
                            {t("backoffice.common.external_id", "External ID")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "type" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("type")}
                        >
                            {t("backoffice.common.type", "Type")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.iconColumn}>
                            {t("backoffice.cmp_product.sms", "SMS")}
                        </DataTable.Title>
                        <DataTable.Title numeric>
                            {t("common.amount", "Amount")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.sequenceColumn} numeric>
                            {t("backoffice.common.sequence", "Sequence")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "createdAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("createdAt")}
                        >
                            {t("common.created_at", "Created at")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "updatedAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("updatedAt")}
                        >
                            {t("common.updated_at", "Updated at")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.iconColumn} numeric>
                            <Icon name="sort" color={theme.colors.black} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? (
                        <Loading />
                    ) : data.cmpProducts.data.length === 0 ? (
                        <DataTable.Row style={theme.styles.oddRow}>
                            <DataTable.Cell>
                                {t(
                                    "backoffice.cmp_product.no_data",
                                    "No products found."
                                )}
                            </DataTable.Cell>
                        </DataTable.Row>
                    ) : (
                        data?.cmpProducts.data.map(renderCmpProductRow)
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.cmpProducts.pagination.pages}
                        itemCount={data?.cmpProducts.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = () => ({
    iconColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 60 },
    sequenceColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 180 },
});
